import React, { useMemo, useState } from 'react';
import { View, Text, StyleSheet, TouchableOpacity, ActivityIndicator, Alert } from 'react-native';
import { SafeAreaView } from 'react-native-safe-area-context';
import type { NativeStackNavigationProp } from '@react-navigation/native-stack';
import { Ionicons } from '@expo/vector-icons';
import * as ImagePicker from 'expo-image-picker';
import * as ImageManipulator from 'expo-image-manipulator';
import { analyzeImage } from '../lib/gemini';
import type { AnalyzedItem } from '../lib/gemini';
import type { RootStackParamList } from '../types/navigation';
import { useTheme } from '../styles/ThemeProvider';

type Props = {
  navigation: NativeStackNavigationProp<RootStackParamList, 'Appraise'>;
};

type AnalysisStep = 'uploading' | 'analyzing' | 'pricing';

/**
 * AppraiseScreen - Allows users to scan items and see valuations WITHOUT signing up.
 * Uses client-side Gemini for quick estimates. Shows price RANGE with sign-up CTA.
 * 
 * This is the "wow moment" acquisition hook - users can try the app before committing.
 */
export default function AppraiseScreen({ navigation }: Props) {
  const theme = useTheme();
  const styles = useMemo(
    () =>
      StyleSheet.create({
        container: {
          flex: 1,
          backgroundColor: theme.colors.backgroundPrimary,
        },
        header: {
          flexDirection: 'row',
          alignItems: 'center',
          justifyContent: 'space-between',
          paddingVertical: theme.spacing.md,
          paddingHorizontal: theme.spacing.lg,
          borderBottomWidth: theme.borders.borderWidth,
          borderBottomColor: theme.borders.borderColor,
          backgroundColor: theme.colors.backgroundPrimary,
        },
        title: {
          ...theme.typography.h3,
          color: theme.colors.textPrimary,
        },
        options: {
          flex: 1,
          padding: theme.spacing.xl,
          gap: theme.spacing.lg,
        },
        heroText: {
          ...theme.typography.lg,
          color: theme.colors.textSecondary,
          textAlign: 'center',
          marginBottom: theme.spacing.lg,
        },
        option: {
          backgroundColor: theme.colors.backgroundSurface,
          borderWidth: theme.borders.borderWidth,
          borderColor: theme.borders.borderColor,
          borderRadius: theme.radius.radiusMd,
          padding: theme.spacing.xl,
          alignItems: 'center',
        },
        iconContainer: {
          width: 64,
          height: 64,
          borderRadius: theme.radius.radiusMd,
          backgroundColor: theme.colors.accent,
          justifyContent: 'center',
          alignItems: 'center',
          marginBottom: theme.spacing.md,
        },
        optionTitle: {
          ...theme.typography.h4,
          color: theme.colors.textPrimary,
          marginBottom: theme.spacing.xs,
        },
        optionSubtitle: {
          ...theme.typography.sm,
          color: theme.colors.textSecondary,
          textAlign: 'center',
        },
        note: {
          ...theme.typography.sm,
          color: theme.colors.textSecondary,
          textAlign: 'center',
          marginTop: theme.spacing.lg,
          marginBottom: theme.spacing.sm,
          paddingHorizontal: theme.spacing.xl,
        },
        signUpPrompt: {
          marginTop: theme.spacing.xl,
          alignItems: 'center',
          paddingTop: theme.spacing.lg,
          borderTopWidth: theme.borders.borderWidth,
          borderTopColor: theme.borders.borderColor,
        },
        signUpText: {
          ...theme.typography.sm,
          color: theme.colors.textSecondary,
          textAlign: 'center',
          marginBottom: theme.spacing.sm,
        },
        signUpLink: {
          ...theme.typography.md,
          fontFamily: theme.fontFamily.bodyMedium,
          color: theme.colors.accent,
          textDecorationLine: 'underline',
        },
        loadingContainer: {
          flex: 1,
          justifyContent: 'center',
          alignItems: 'center',
          padding: theme.spacing.xl,
          backgroundColor: theme.colors.backgroundPrimary,
        },
        loadingSpinner: {
          marginBottom: theme.spacing.lg,
        },
        loadingTitle: {
          ...theme.typography.h4,
          color: theme.colors.textPrimary,
          marginBottom: theme.spacing.xl,
        },
        checklistContainer: {
          width: '100%',
          maxWidth: 400,
          backgroundColor: theme.colors.backgroundSurface,
          borderWidth: theme.borders.borderWidth,
          borderColor: theme.borders.borderColor,
          borderRadius: theme.radius.radiusMd,
          padding: theme.spacing.lg,
        },
        checklistItem: {
          flexDirection: 'row',
          alignItems: 'center',
          paddingVertical: theme.spacing.md,
          borderBottomWidth: theme.borders.borderWidth,
          borderBottomColor: theme.borders.borderColor,
        },
        checklistIcon: {
          fontSize: 20,
          width: 32,
          fontFamily: theme.fontFamily.body,
          color: theme.colors.textTertiary,
        },
        checklistIconComplete: {
          color: theme.colors.accent,
        },
        checklistIconActive: {
          color: theme.colors.accent,
        },
        checklistText: {
          flex: 1,
          ...theme.typography.md,
          color: theme.colors.textTertiary,
        },
        checklistTextComplete: {
          color: theme.colors.textSecondary,
          textDecorationLine: 'line-through',
        },
        checklistTextActive: {
          color: theme.colors.textPrimary,
          fontFamily: theme.fontFamily.bodyMedium,
        },
      }),
    [theme],
  );
  const [loading, setLoading] = useState(false);
  const [currentStep, setCurrentStep] = useState<AnalysisStep>('uploading');
  const [completedSteps, setCompletedSteps] = useState<Set<AnalysisStep>>(new Set());

  const markStepComplete = (step: AnalysisStep) => {
    setCompletedSteps(prev => new Set([...prev, step]));
  };

  const setAnalysisStep = (step: AnalysisStep) => {
    setCurrentStep(step);
  };

  const resetAnalysisSteps = () => {
    setCompletedSteps(new Set());
    setCurrentStep('uploading');
  };

  const handleCameraPress = async () => {
    try {
      const { status } = await ImagePicker.requestCameraPermissionsAsync();
      if (status !== 'granted') {
        Alert.alert('Permission Denied', 'Sorry, we need camera permissions to make this work!');
        return;
      }

      const result = await ImagePicker.launchCameraAsync({
        mediaTypes: ImagePicker.MediaTypeOptions.Images,
        aspect: [4, 3],
        quality: 0.8,
      });

      if (!result.canceled && result.assets && result.assets.length > 0) {
        await processAndAnalyzeImage(result.assets[0].uri);
      }
    } catch (error) {
      console.error('[APPRAISE] Camera Error:', error);
      Alert.alert('Camera Error', 'Failed to access camera. Please try again.');
    }
  };

  const handleGalleryPress = async () => {
    try {
      const { status } = await ImagePicker.requestMediaLibraryPermissionsAsync();
      if (status !== 'granted') {
        Alert.alert('Permission Denied', 'Sorry, we need gallery permissions to make this work!');
        return;
      }

      const result = await ImagePicker.launchImageLibraryAsync({
        mediaTypes: ImagePicker.MediaTypeOptions.Images,
        aspect: [4, 3],
        quality: 0.8,
      });

      if (!result.canceled && result.assets && result.assets.length > 0) {
        await processAndAnalyzeImage(result.assets[0].uri);
      }
    } catch (error) {
      console.error('[APPRAISE] Gallery Error:', error);
      Alert.alert('Gallery Error', 'Failed to access gallery. Please try again.');
    }
  };

  const processAndAnalyzeImage = async (imageUri: string) => {
    resetAnalysisSteps();
    setLoading(true);
    setAnalysisStep('uploading');

    try {
      console.log('[APPRAISE] Starting client-side Gemini analysis:', imageUri);

      // Resize and convert to base64
      const manipResult = await ImageManipulator.manipulateAsync(
        imageUri,
        [{ resize: { width: 512 } }],
        {
          compress: 0.9,
          format: ImageManipulator.SaveFormat.JPEG,
          base64: true,
        }
      );

      const base64Data = manipResult.base64!;
      console.log('[APPRAISE] Image prepared, base64 length:', base64Data.length);

      markStepComplete('uploading');
      setAnalysisStep('analyzing');

      // Use client-side Gemini for quick estimate (no backend, no account needed)
      const analyzedItems = await analyzeImage(base64Data);

      markStepComplete('analyzing');
      setAnalysisStep('pricing');

      // Small delay for UX
      await new Promise(resolve => setTimeout(resolve, 500));
      markStepComplete('pricing');

      if (!analyzedItems || analyzedItems.length === 0) {
        Alert.alert(
          'No Items Found',
          'We couldn\'t identify any items in this image. Try taking a clearer photo with good lighting.',
          [{ text: 'Try Again', onPress: () => { setLoading(false); resetAnalysisSteps(); } }]
        );
        return;
      }

      console.log('[APPRAISE] Gemini analysis complete:', analyzedItems[0]?.name);

      // Navigate to results screen with analyzed item and image (including base64 for persistence)
      navigation.navigate('AppraiseResult', {
        items: analyzedItems.map(item => ({ ...item, selected: true })),
        imageUri: manipResult.uri,
        imageBase64: base64Data, // Pass base64 for saving across sign-in
      });

    } catch (error: any) {
      console.error('[APPRAISE] Analysis failed:', error);
      Alert.alert(
        'Analysis Failed',
        error.message || 'Unable to analyze the image. Please try again.',
        [{ text: 'OK' }]
      );
    } finally {
      setLoading(false);
      resetAnalysisSteps();
    }
  };

  const getStepLabel = (step: AnalysisStep): string => {
    const labels = {
      uploading: 'Processing image',
      analyzing: 'Analyzing with AI',
      pricing: 'Estimating value',
    };
    return labels[step];
  };

  const getStepIcon = (step: AnalysisStep): string => {
    if (completedSteps.has(step)) return '✓';
    if (currentStep === step) return '⏳';
    return '○';
  };

  // Loading UI
  if (loading) {
    const steps: AnalysisStep[] = ['uploading', 'analyzing', 'pricing'];
    
    return (
      <View style={styles.loadingContainer}>
        <ActivityIndicator size={36} color={theme.colors.accent} style={styles.loadingSpinner} />
        <Text style={styles.loadingTitle}>Processing...</Text>
        <View style={styles.checklistContainer}>
          {steps.map((step) => (
            <View key={step} style={styles.checklistItem}>
              <Text style={[
                styles.checklistIcon,
                completedSteps.has(step) && styles.checklistIconComplete,
                currentStep === step && styles.checklistIconActive,
              ]}>
                {getStepIcon(step)}
              </Text>
              <Text style={[
                styles.checklistText,
                completedSteps.has(step) && styles.checklistTextComplete,
                currentStep === step && styles.checklistTextActive,
              ]}>
                {getStepLabel(step)}
              </Text>
            </View>
          ))}
        </View>
      </View>
    );
  }

  // Main UI - matches AddItemScreen style
  return (
    <SafeAreaView style={styles.container} edges={['top']}>
      <View style={styles.header}>
        <TouchableOpacity onPress={() => navigation.goBack()}>
          <Ionicons name="arrow-back" size={24} color={theme.colors.textPrimary} />
        </TouchableOpacity>
        <Text style={styles.title}>Quick Appraise</Text>
        <View style={{ width: 24 }} />
      </View>

      <View style={styles.options}>
        <Text style={styles.heroText}>
          See what your stuff is worth — no sign-up required
        </Text>

        <TouchableOpacity style={styles.option} onPress={handleCameraPress}>
          <View style={styles.iconContainer}>
            <Ionicons name="camera" size={40} color={theme.colors.backgroundPrimary} />
          </View>
          <Text style={styles.optionTitle}>Take Photo</Text>
          <Text style={styles.optionSubtitle}>Snap a pic of any item</Text>
        </TouchableOpacity>

        <TouchableOpacity style={styles.option} onPress={handleGalleryPress}>
          <View style={styles.iconContainer}>
            <Ionicons name="images-outline" size={40} color={theme.colors.backgroundPrimary} />
          </View>
          <Text style={styles.optionTitle}>Choose from Gallery</Text>
          <Text style={styles.optionSubtitle}>Select an existing photo</Text>
        </TouchableOpacity>

        <Text style={styles.note}>
          💡 Tip: Use good lighting and show any brand labels for best results.
        </Text>

        <View style={styles.signUpPrompt}>
          <Text style={styles.signUpText}>
            Want to save items and track your portfolio?
          </Text>
          <TouchableOpacity onPress={() => navigation.navigate('SignUp')}>
            <Text style={styles.signUpLink}>Create a free account →</Text>
          </TouchableOpacity>
        </View>
      </View>
    </SafeAreaView>
  );
}

